import { Link } from "react-router-dom";
import { Facebook, Linkedin, Twitter, Mail, Phone, MapPin, Clock, ArrowRight } from "lucide-react";
import { SITE } from "@/lib/site";
import { useState } from "react";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubscribe(e) {
    e.preventDefault();
    if (!email) return;
    try {
      setLoading(true);
      await subscribeNewsletter(email);
      toast.success("You're subscribed.", { description: "We'll keep you informed of important tax & financial updates." });
      setEmail("");
    } catch (err) {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <footer data-testid="site-footer" className="bg-[var(--trj-navy)] text-white">
      <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-12">
        {/* Brand + Newsletter */}
        <div className="lg:col-span-5">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src={SITE.assets.logo} alt="TRJ Services" className="h-14 bg-white p-2" />
          </Link>
          <p className="mt-6 text-[14px] text-white/70 leading-relaxed max-w-md">
            Financial Strategy &amp; Business Solutions helping individuals, businesses, nonprofits, and ministries
            build the systems, oversight, and strategy required for long-term success.
          </p>

          <form onSubmit={onSubscribe} className="mt-8 max-w-md" data-testid="footer-newsletter-form">
            <div className="trj-eyebrow !text-[var(--trj-gold)] mb-3">Stay Informed</div>
            <div className="flex items-stretch border border-white/20 bg-white/5">
              <input
                type="email"
                required
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none"
                data-testid="footer-newsletter-email"
              />
              <button
                type="submit"
                disabled={loading}
                className="px-5 bg-[var(--trj-green)] hover:bg-[var(--trj-green-dark)] disabled:opacity-60 transition-colors flex items-center gap-1"
                data-testid="footer-newsletter-submit"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>

          <div className="mt-8 flex items-center gap-3">
            <a href={SITE.socials.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[var(--trj-gold)] hover:text-[var(--trj-gold)] transition-colors" data-testid="footer-social-facebook">
              <Facebook className="w-4 h-4" />
            </a>
            <a href={SITE.socials.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[var(--trj-gold)] hover:text-[var(--trj-gold)] transition-colors" data-testid="footer-social-linkedin">
              <Linkedin className="w-4 h-4" />
            </a>
            <a href={SITE.socials.twitter} target="_blank" rel="noreferrer" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[var(--trj-gold)] hover:text-[var(--trj-gold)] transition-colors" data-testid="footer-social-twitter">
              <Twitter className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Nav */}
        <div className="lg:col-span-4 grid grid-cols-2 gap-8 text-sm">
          <div>
            <div className="trj-eyebrow !text-[var(--trj-gold)] mb-4">Navigate</div>
            <ul className="space-y-2 text-white/75">
              <li><Link to="/" className="hover:text-[var(--trj-gold)]">Home</Link></li>
              <li><Link to="/about" className="hover:text-[var(--trj-gold)]">About</Link></li>
              <li><Link to="/solutions" className="hover:text-[var(--trj-gold)]">Solutions</Link></li>
              <li><Link to="/executive-financial-command" className="hover:text-[var(--trj-gold)]">Executive Financial Command™</Link></li>
              <li><Link to="/tax-school" className="hover:text-[var(--trj-gold)]">Tax School</Link></li>
              <li><Link to="/store" className="hover:text-[var(--trj-gold)]">Store</Link></li>
              <li><Link to="/resources" className="hover:text-[var(--trj-gold)]">Resources</Link></li>
              <li><Link to="/contact" className="hover:text-[var(--trj-gold)]">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="trj-eyebrow !text-[var(--trj-gold)] mb-4">Solutions</div>
            <ul className="space-y-2 text-white/75">
              <li><Link to="/solutions#individual" className="hover:text-[var(--trj-gold)]">Individual Tax</Link></li>
              <li><Link to="/solutions#business" className="hover:text-[var(--trj-gold)]">Business Financial</Link></li>
              <li><Link to="/solutions#nonprofit" className="hover:text-[var(--trj-gold)]">Nonprofit & Ministry</Link></li>
              <li><Link to="/solutions#infrastructure" className="hover:text-[var(--trj-gold)]">Business Foundation Services</Link></li>
              <li><Link to="/executive-financial-command" className="hover:text-[var(--trj-gold)]">Executive Financial Command™</Link></li>
            </ul>
          </div>
        </div>

        {/* Contact */}
        <div className="lg:col-span-3">
          <div className="trj-eyebrow !text-[var(--trj-gold)] mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-[var(--trj-gold)]" /><a href={SITE.phoneHref} className="hover:text-white">{SITE.phone}</a></li>
            <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-[var(--trj-gold)]" /><a href={SITE.emailHref} className="hover:text-white">{SITE.email}</a></li>
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[var(--trj-gold)]" /><span>{SITE.address}</span></li>
            <li className="flex items-start gap-3"><Clock className="w-4 h-4 mt-0.5 text-[var(--trj-gold)]" /><span>{SITE.hours}</span></li>
          </ul>
          <a
            href={SITE.calendlyUrl}
            target="_blank"
            rel="noreferrer"
            className="trj-btn-green text-sm mt-6 w-full justify-center"
            data-testid="footer-schedule-consultation-btn"
          >
            Schedule Consultation
          </a>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-white/55">
          <div>© {new Date().getFullYear()} TRJ Services. All rights reserved.</div>
          <div className="trj-eyebrow !text-[var(--trj-gold)]">{SITE.motto}</div>
        </div>
      </div>
    </footer>
  );
}
